/** One consultant's timesheet entries, day by day — shared by Reports → Timesheets and the consultant page. */
import { Box, Typography } from "@mui/material";
import { T } from "./tokens";
import type { TimeEntry } from "./data";

export const fmtHours = (m: number) => (m > 0 ? `${(m / 60).toFixed(1)}h` : "—");
const fmtDay = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

const GRID = "92px 96px minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 2fr) 56px";
const head = { fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted } as const;
const cell = { fontSize: "12px", color: T.ink2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;

const TimesheetEntries = ({ entries }: { entries: TimeEntry[] }) => {
    const days = new Map<string, TimeEntry[]>();
    [...entries]
        .sort((a, b) => a.date.localeCompare(b.date) || (a.start || "").localeCompare(b.start || ""))
        .forEach((e) => days.set(e.date, [...(days.get(e.date) ?? []), e]));
    const total = entries.reduce((n, e) => n + e.minutes, 0);

    if (entries.length === 0) return <Typography sx={{ fontSize: "12.5px", color: T.muted }}>Nothing logged in this period.</Typography>;

    return (
        <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: "760px" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: GRID, gap: "10px", padding: "0 0 8px", borderBottom: `1px solid ${T.border}` }}>
                    {["Day", "Time", "Activity", "Advisory type", "Account", "Description"].map((h) => <Typography key={h} sx={head}>{h}</Typography>)}
                    <Typography sx={{ ...head, textAlign: "right" }}>Hours</Typography>
                </Box>
                {[...days.entries()].map(([date, list]) => (
                    <Box key={date} sx={{ borderBottom: `1px solid ${T.borderSoft}` }}>
                        {list.map((e, i) => (
                            <Box key={i} sx={{ display: "grid", gridTemplateColumns: GRID, gap: "10px", alignItems: "center", padding: "6px 0" }}>
                                <Typography sx={{ ...cell, fontWeight: 700 }}>{i === 0 ? fmtDay(date) : ""}</Typography>
                                <Typography sx={{ ...cell, color: T.muted, fontVariantNumeric: "tabular-nums" }}>{e.start && e.end ? `${e.start}–${e.end}` : "no end time"}</Typography>
                                <Typography sx={cell} title={e.activityType || undefined}>{e.activityType || "not set"}</Typography>
                                <Typography sx={{ ...cell, color: e.advisoryType ? T.ink2 : T.muted2 }} title={e.advisoryType || undefined}>{e.advisoryType || "—"}</Typography>
                                <Typography sx={{ ...cell, color: e.accountName ? T.ink2 : T.muted2 }} title={e.accountName || undefined}>{e.accountName || "—"}</Typography>
                                <Typography sx={{ ...cell, color: T.muted }} title={e.description || undefined}>{e.description || "—"}</Typography>
                                <Typography sx={{ ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtHours(e.minutes)}</Typography>
                            </Box>
                        ))}
                        {list.length > 1 && (
                            <Box sx={{ display: "grid", gridTemplateColumns: GRID, gap: "10px", padding: "0 0 6px" }}>
                                <Typography sx={{ ...cell, gridColumn: "1 / 7", textAlign: "right", fontSize: "11px", color: T.muted }}>day total</Typography>
                                <Typography sx={{ ...cell, textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{fmtHours(list.reduce((n, e) => n + e.minutes, 0))}</Typography>
                            </Box>
                        )}
                    </Box>
                ))}
                <Box sx={{ display: "grid", gridTemplateColumns: GRID, gap: "10px", padding: "8px 0 0", borderTop: `2px solid ${T.border}` }}>
                    <Typography sx={{ ...cell, gridColumn: "1 / 7", fontWeight: 800 }}>
                        {entries.length} entries · {days.size} days
                    </Typography>
                    <Typography sx={{ ...cell, textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{fmtHours(total)}</Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default TimesheetEntries;
